/**
 * Error utilities and types for the application
 */
import { AxiosError } from 'axios'
import i18n from '@/i18n'

const { t } = i18n.global

/**
 * Error categories for the application
 */
export enum ErrorCategory {
  NETWORK = 'network',
  API = 'api',
  VALIDATION = 'validation',
  AUTHENTICATION = 'authentication',
  AUTHORIZATION = 'authorization',
  TIMEOUT = 'timeout',
  SERVER = 'server',
  CLIENT = 'client',
  UNKNOWN = 'unknown'
}

/**
 * Error severity levels
 */
export enum ErrorSeverity {
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
  CRITICAL = 'critical'
}

/**
 * Application error interface
 */
export interface AppError {
  message: string
  category: ErrorCategory
  severity: ErrorSeverity
  originalError?: Error | AxiosError | unknown
  code?: string
  statusCode?: number
  retry?: boolean
  retryCount?: number
  timestamp: number
  context?: Record<string, any>
}

/**
 * Create a network error
 */
export function createNetworkError(error: unknown, context?: Record<string, any>): AppError {
  const isAxiosError = error instanceof Error && 'isAxiosError' in error

  // Handle different network error types
  if (isAxiosError && (error as AxiosError).code === 'ECONNABORTED') {
    return {
      message: t('errors.timeout'),
      category: ErrorCategory.TIMEOUT,
      severity: ErrorSeverity.ERROR,
      originalError: error,
      code: 'TIMEOUT',
      retry: true,
      timestamp: Date.now(),
      context
    }
  }

  if (isAxiosError && (error as AxiosError).message.includes('Network Error')) {
    return {
      message: t('errors.networkError'),
      category: ErrorCategory.NETWORK,
      severity: ErrorSeverity.ERROR,
      originalError: error,
      code: 'NETWORK_ERROR',
      retry: true,
      timestamp: Date.now(),
      context
    }
  }

  return {
    message: t('common.error'),
    category: ErrorCategory.NETWORK,
    severity: ErrorSeverity.ERROR,
    originalError: error,
    code: 'UNKNOWN_NETWORK_ERROR',
    retry: true,
    timestamp: Date.now(),
    context
  }
}

/**
 * Create an API error from an Axios error
 */
export function createApiError(error: AxiosError, context?: Record<string, any>): AppError {
  const statusCode = error.response?.status
  let category = ErrorCategory.API
  let severity = ErrorSeverity.ERROR
  let message = t('common.error')
  let code = 'API_ERROR'
  let retry = false

  // Handle different status codes
  if (statusCode) {
    if (statusCode === 401) {
      category = ErrorCategory.AUTHENTICATION
      message = t('errors.unauthorized')
      code = 'UNAUTHORIZED'
    } else if (statusCode === 403) {
      category = ErrorCategory.AUTHORIZATION
      message = t('errors.forbidden')
      code = 'FORBIDDEN'
    } else if (statusCode === 404) {
      category = ErrorCategory.API
      message = t('errors.notFound')
      code = 'NOT_FOUND'
    } else if (statusCode >= 500) {
      category = ErrorCategory.SERVER
      message = t('errors.serverError')
      code = 'SERVER_ERROR'
      retry = true
    } else if (statusCode === 400) {
      category = ErrorCategory.VALIDATION
      message = t('errors.validationError')
      code = 'VALIDATION_ERROR'
    } else if (statusCode === 429) {
      category = ErrorCategory.API
      message = t('errors.tooManyRequests')
      code = 'TOO_MANY_REQUESTS'
      retry = true
    }
  }

  // Try to get a more specific error message from the response
  try {
    const responseData = error.response?.data
    if (responseData && typeof responseData === 'object') {
      // Use type assertion to access properties safely
      const typedData = responseData as Record<string, any>
      if (typedData.message && typeof typedData.message === 'string') {
        message = typedData.message
      } else if (typedData.error && typeof typedData.error === 'string') {
        message = typedData.error
      }
    }
  } catch (e) {
    // Ignore parsing errors
  }

  return {
    message,
    category,
    severity,
    originalError: error,
    code,
    statusCode,
    retry,
    timestamp: Date.now(),
    context
  }
}

/**
 * Check if an error is retryable
 */
export function isRetryableError(error: AppError): boolean {
  return !!error.retry
}

/**
 * Get appropriate retry delay based on retry count
 */
export function getRetryDelay(retryCount: number): number {
  // Exponential backoff with jitter
  const baseDelay = 1000 // 1 second
  const maxDelay = 30000 // 30 seconds
  const exponentialDelay = Math.min(maxDelay, baseDelay * Math.pow(2, retryCount))
  const jitter = Math.random() * 0.3 * exponentialDelay // Add up to 30% jitter

  return Math.floor(exponentialDelay + jitter)
}
